import { useParams } from 'react-router-dom';

import { Loader } from '@hivebrite/design-system/Loader';
import { toast } from '@hivebrite/design-system/Toast';
import { useScrollTop } from '@hivebrite/react-hooks';

import CreateEditLayout from 'hb-react/frontoffice/apps/Forum/components/CreateEditLayout';
import DiscussionNotFound from 'hb-react/frontoffice/apps/Forum/components/DiscussionNotFound';
import CreateEditForm from 'hb-react/frontoffice/apps/Forum/containers/CreateEditForm';
import { useGetEditDiscussion } from 'hb-react/frontoffice/apps/Forum/hooks/queries/useGetEditDiscussion';
import { useUpdateDiscussion } from 'hb-react/frontoffice/apps/Forum/hooks/queries/useUpdateDiscussion';
import {
  type Discussion,
  type FormValues,
} from 'hb-react/frontoffice/apps/Forum/types';

const ForumEdit = () => {
  const { id } = useParams() as { id: string };
  const { discussion, isLoading, isError, error } = useGetEditDiscussion(
    parseInt(id, 10),
  );

  useScrollTop();

  if (isLoading) {
    return (
      <CreateEditLayout>
        <Loader size={32} />
      </CreateEditLayout>
    );
  }

  if (error?.request.status === 404) {
    return (
      <CreateEditLayout>
        <DiscussionNotFound />
      </CreateEditLayout>
    );
  }

  if (isError) {
    toast.error(__('Sorry, an error occurred'));
  }

  if (isError || !discussion) {
    return null;
  }

  return <EditPage discussion={discussion} />;
};

interface EditPageFormProps {
  coverPictureUrl?: Discussion['cover_picture_url'];
  id: Discussion['id'];
  initialValues: FormValues;
}

const EditPageForm = ({
  id,
  initialValues,
  coverPictureUrl,
}: EditPageFormProps) => {
  const { mutate } = useUpdateDiscussion(id);

  return (
    <CreateEditForm
      title={s__('Forum|Edit a discussion')}
      initialValues={initialValues}
      coverPictureUrl={coverPictureUrl}
      mutate={mutate}
      showUserTargetingButton={false}
    />
  );
};

interface EditPageProps {
  discussion: Discussion;
}

const EditPage = ({ discussion }: EditPageProps) => {
  const {
    id,
    title,
    content,
    storage_attachments: storageAttachements,
    storage_uploads: storageUploads,
    categories,
    cover_picture_url: coverPictureUrl,
  } = discussion;

  const initialValues = {
    category_ids: categories.map((cat) => cat.id),
    content,
    storage_attachments: storageAttachements || [],
    storage_uploads: storageUploads || [],
    title,
  };

  return (
    <CreateEditLayout>
      <EditPageForm
        initialValues={initialValues}
        coverPictureUrl={coverPictureUrl}
        id={id}
      />
    </CreateEditLayout>
  );
};

export default ForumEdit;
